import { ScrollView, View } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import Scroll from 'src/game/components/shared/Scroll';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { animDuration } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class ScrollShortcuts<T extends Scroll<any, any>> {
  public readonly scroll: T;
  public readonly topButton: ButtonScaleViewWithText;
  public readonly bottomButton: ButtonScaleViewWithText;
  private timeout?: ReturnType<typeof setTimeout>;

  constructor(scroll: T) {
    this.scroll = scroll;

    const scrollView = this.scroll.getView();
    const superview = scrollView.getSuperview();
    if (!superview) throw new Error('Scroll must have a superview');

    this.topButton = new ButtonScaleViewWithText({
      superview,
      image:
        'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/scroll_shortcut.png',
      imgScale: DOWNSCALE_FACTOR,
      fontSize: 30,
      width: downscale(300),
      height: downscale(95),
      font: 'Body',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
      },
      pressedOffsetY: 5,
      centerOnOrigin: true,
      labelOffsetY: -3,
      x: scrollView.style.x + scrollView.style.width * 0.5,
      y: scrollView.style.y + 50,
      localeText: () => i18n('weeklyLeaderboard.players.top'),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.TOP_BUTTON,
      },
      zIndex: 100,
      visible: false,
      onClick: async () => {
        await this.scroll.centerOn(0, animDuration);
      },
    });

    this.bottomButton = new ButtonScaleViewWithText({
      superview,
      image:
        'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/scroll_shortcut.png',
      imgScale: DOWNSCALE_FACTOR,
      fontSize: 30,
      width: downscale(300),
      height: downscale(95),
      font: 'Body',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
      },
      pressedOffsetY: 5,
      centerOnOrigin: true,
      labelOffsetY: -3,
      x: scrollView.style.x + scrollView.style.width * 0.5,
      y: scrollView.style.y + scrollView.style.height - 50,
      localeText: () => i18n('weeklyLeaderboard.players.bottom'),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.BOTTOM_BUTTON,
      },
      zIndex: 100,
      visible: false,
      onClick: async () => {
        await this.scroll.centerOn(this.scroll.itemCount - 1, animDuration);
      },
    });

    scrollView.addListener('Scrolled', () => {
      const lastPos = this.scroll.getElementPosition(this.scroll.itemCount - 1);
      const scrollHeight = this.scroll.getView().style.height;
      // No need to show the buttons if the content fits in the scroll view
      if (lastPos < scrollHeight) return;
      this.showButtons();
    });
  }

  public showButtons() {
    this.topButton.show();
    this.bottomButton.show();
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.hideButtons(), 2000);
  }

  public hideButtons() {
    this.topButton.hide();
    this.bottomButton.hide();
    clearTimeout(this.timeout);
    this.timeout = undefined;
  }
}
